import { z } from 'zod/v4';

// Replace z.any() with proper types
const ParameterSettingSchema = z.object({
    enabled: z.boolean().default(false),
    initialValue: z.string().optional(),
    excludes: z.array(z.string()).default([]),
});

export const VisualizationParametersSchema = z.object({
    groups: ParameterSettingSchema.optional(),
    timeframe: ParameterSettingSchema.optional(),
    transform: ParameterSettingSchema.optional(),
});

// Type helper for parameter extraction
export type VisualizationParameters = z.infer<typeof VisualizationParametersSchema>;

// Parameter value schemas for runtime validation
const ParameterValueSchema = z.object({
    groups: z.string().optional(),
    timeframe: z.string().optional(),
    transform: z.string().optional(),
});

export type ParameterValues = z.infer<typeof ParameterValueSchema>;

// Helper to validate parameter compatibility
const validateParameterCompatibility = (parameters: VisualizationParameters, values: ParameterValues): boolean => {
    // Check if all provided values have corresponding enabled parameters
    for (const [key, value] of Object.entries(values)) {
        if (value !== undefined) {
            const paramKey = key as keyof VisualizationParameters;
            const paramSetting = parameters[paramKey];

            if (!paramSetting?.enabled) {
                return false;
            }

            // Check if value is in excludes list
            if (paramSetting.excludes.includes(value)) {
                return false;
            }
        }
    }

    return true;
};

// Create a schema that validates parameters against their settings
const createParameterValidator = (settings: VisualizationParameters) =>
    ParameterValueSchema.refine(values => validateParameterCompatibility(settings, values), {
        message: 'Parameter values do not match the configured settings',
    });

export { createParameterValidator };
